import { signal, Signal } from '@preact/signals-react'
import { BlockComponentRegistry } from './BlockComponentRegistry'
import { BlockFactory } from './BlockFactory'
import { BlockOptions } from './ITaskBlock'
import { ITaskBlock } from './ITaskBlock'

export class BlockEngine {
    private blocks: Map<string, ITaskBlock> = new Map()
    /* 
    Hidden blocks are blocks that are not visible to the user, but are still part of the block engine.
    They are used as a template for new blocks. For example, a list block will create a hidden block for each item in the list.
    */
    private hiddenBlocks: Map<string, ITaskBlock> = new Map()
    private editable: Signal<boolean> = signal(true)

    constructor() {
        this.blocks = new Map()
        this.hiddenBlocks = new Map()
    }

    setEditable(value: boolean): void {
        this.editable.value = value
    }

    isEditable(): boolean {
        return this.editable.value
    }

    getEditableSignal(): Signal<boolean> {
        return this.editable
    }

    addBlock(blockType: string, options: BlockOptions, isHidden: boolean = false) {
        const blockComponent = BlockComponentRegistry.getInstance().getBlock(blockType)
        if (!blockComponent) {
            throw new Error(`Block component ${blockType} not registered`)
        }
        const block = BlockFactory.createBlock({
            type: blockType,
            id: options.id,
            name: options.name,
            component: blockComponent.component,
            componentProps: options.componentProps,
        })
        if (isHidden) {
            this.hiddenBlocks.set(options.id, block)
        } else {
            this.blocks.set(options.id, block)
        }
        return block
    }

    removeBlock(blockId: string) {
        const block = this.blocks.get(blockId)
        if (!block) {
            console.warn(`Block ${blockId} not found`)
        }
        if (block) {
            block.clear?.()
            this.blocks.delete(blockId)
        }
    }

    getBlock(blockId: string): ITaskBlock | undefined {
        return this.blocks.get(blockId)
    }

    getBlockByName(name: string): ITaskBlock[] | undefined {
        return Array.from(this.blocks.values()).filter(block => block.name === name)
    }

    getBlocks(blockIds: string[]) {
        return blockIds
            .map(blockId => this.blocks.get(blockId))
            .filter(block => block !== undefined)
    }

    getAllBlocks() {
        return Array.from(this.blocks.values())
    }

    showBlock(blockId: string) {
        const block = this.blocks.get(blockId)
        if (!block) {
            console.warn(`Block ${blockId} not found`)
        }
        if (block) {
            block.show()
        }
    }

    hideBlock(blockId: string) {
        const block = this.blocks.get(blockId)
        if (!block) {
            console.warn(`Block ${blockId} not found`)
        }
        if (block) {
            block.hide()
        }
    }

    updateBlock(blockId: string, newProps?: Record<string, unknown>) {
        const block = this.blocks.get(blockId)
        if (!block) {
            console.warn(`Block ${blockId} not found`)
            return
        }

        if (newProps && block.updateProps) {
            block.updateProps(newProps)
        }
        block.render()
    }

    clear() {
        this.blocks.forEach(block => {
            block.clear?.()
        })
        this.blocks.clear()
        this.clearHiddenBlocks()
    }

    getHiddenBlock(blockId: string): ITaskBlock | undefined {
        return this.hiddenBlocks.get(blockId)
    }

    getAllHiddenBlocks(): ITaskBlock[] {
        return Array.from(this.hiddenBlocks.values())
    }

    clearHiddenBlocks(): void {
        this.hiddenBlocks.forEach(block => {
            block.clear?.()
        })
        this.hiddenBlocks.clear()
    }
}
